@file:OptIn(DelicateApi::class)

package club.srams.website.components.form

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import club.srams.website.components.utility.Constant.INFO_EMAIL_ID
import com.coding.meet.util.Theme
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.css.resize
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.InputGroup
import com.varabyte.kobweb.silk.components.forms.Label
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.AlignSelf
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontFamily
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.*

@Composable
fun ContactForm(
    toEmailAddress: String
) {
//    val validator = remember {
//        mutableListOf<FormInput>().apply {
//            add(FormInput("inputName"))
//            add(FormInput("inputEmail"))
//            add(FormInput("inputMessage"))
//        }
//    }
//    val breakpoint = rememberBreakpoint()

    // ── Form state ──────────────────────────────────────────────────────────
    var name    by remember { mutableStateOf("") }
    var subject by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }

    // Touched flags – show errors only after the user has interacted with a field
    var nameTouched    by remember { mutableStateOf(false) }
    var subjectTouched by remember { mutableStateOf(false) }
    var messageTouched by remember { mutableStateOf(false) }

    var submitted by remember { mutableStateOf(false) }

    // Validation
    val nameError    = name.isBlank()
    val subjectError = subject.isBlank()
    val messageError = message.isBlank()
    val formValid    = !nameError && !subjectError && !messageError

    val isDark     = ColorMode.current == ColorMode.DARK
    val textColor  = if (isDark) Colors.White else Colors.Black
    val errorColor = Colors.Red


    // ── Contact form ────────────────────────────────────────────────────────
    // Note: Silk has no Form component — the Compose Web dom Form is the correct
    // primitive here. All field components inside it are Silk.
    Form(
        attrs = {
            onSubmit { evt ->
                evt.preventDefault()
                nameTouched    = true
                subjectTouched = true
                messageTouched = true

                if (!formValid) return@onSubmit

                // Open the user's email client with the form data pre-filled.
                // Replace the address below with your club's actual contact email.
                //val to   = toEmailAddress
                val sub  = js("encodeURIComponent")(subject.trim()) as String
                val body = js("encodeURIComponent")(
                    "From: ${name.trim()}\n\n${message.trim()}"
                ) as String

                kotlinx.browser.window.location.href =
                    "mailto:$toEmailAddress?subject=$sub&body=$body"

                submitted = true
            }
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(1.5.cssRem)
                maxWidth(600.px)
                width(100.percent)
                property("margin", "0 auto")
            }
        }
    ) {

        // ── Name field ───────────────────────────────────────────────────────
        // Silk Label uses ElementTarget.NextSibling to wire the `for` attribute
        // to the TextInput that immediately follows it in the DOM.
        Column(Modifier.gap(0.4.cssRem).fillMaxWidth()) {
            Label(
                target = ElementTarget.NextSibling,
                modifier = Modifier.color(textColor),
                label = "Name *"
            )
            InputGroup(Modifier.fillMaxWidth()) {
                TextInput(
                    text          = name,
                    onTextChange  = { value -> name = value; nameTouched = true },
                    placeholder   = "Your full name",
                    modifier      = Modifier
                        .fillMaxWidth()
                        .thenIf(nameTouched && nameError, Modifier.border(1.5.px, LineStyle.Solid, errorColor))
                )
            }
            if (nameTouched && nameError) {
                SpanText("Name is required.", Modifier.color(errorColor).fontSize(0.8.cssRem))
            }
        }

        // ── Subject field ────────────────────────────────────────────────────
        Column(Modifier.gap(0.4.cssRem).fillMaxWidth()) {
            Label(
                target   = ElementTarget.NextSibling,
                modifier = Modifier.color(textColor),
                label    = "Subject *"
            )
            InputGroup(Modifier.fillMaxWidth()) {
                TextInput(
                    text          = subject,
                    onTextChange  = { value -> subject = value; subjectTouched = true },
                    placeholder   = "Tell us about your suggestion?",
                    modifier      = Modifier
                        .fillMaxWidth()
                        .thenIf(subjectTouched && subjectError, Modifier.border(1.5.px, LineStyle.Solid, errorColor))
                )
            }
            if (subjectTouched && subjectError) {
                SpanText("Subject is required.", Modifier.color(errorColor).fontSize(0.8.cssRem))
            }
        }

        // ── Message field ────────────────────────────────────────────────────
        // Silk has no TextArea component — the Compose Web dom TextArea is the
        // correct primitive here. The Label still uses Silk's component.
        Column(
            Modifier.gap(0.4.cssRem)
                .fillMaxWidth()
        ) {
            Label(
                target   = ElementTarget.NextSibling,
                modifier = Modifier.color(textColor),
                label    = "Message *"
            )
            TextArea(
                value = message,
                attrs = {
                    onInput { evt ->
                        message = evt.value
                        messageTouched = true
                    }
                    attr("placeholder", "Write your message here…")
                    attr("rows", "6")
                    style {
                        width(100.percent)
                        padding(0.5.cssRem)
                        borderRadius(4.px)
                        fontSize(1.cssRem)
                        fontFamily("inherit")
                        resize(Resize.Vertical)
                        if (messageTouched && messageError)
                            property("border", "1.5px solid red")
                        else
                            property("border", "1px solid #ccc")
                    }
                }
            )
            if (messageTouched && messageError) {
                SpanText("Message is required.", Modifier.color(errorColor).fontSize(0.8.cssRem))
            }
        }

        // ── Submit button ────────────────────────────────────────────────────
        Button(
            onClick = {}
            , modifier = Modifier
                .alignSelf(AlignSelf.FlexEnd)
                .padding(topBottom = 0.6.cssRem, leftRight = 1.8.cssRem)
            , type = ButtonType.Submit
        ) {
            SpanText("Send Message")
        }
    }

    //HorizontalDivider(Modifier.fillMaxWidth())



}